/**
 * @fileoverview Defines the main App component, serving as the application's root. It configures routing using React Router, incorporating authentication via components like SignIn, SignUp, and PrivateRoute. The App component renders the UI and manages navigation to various pages.
 */

import { lazy } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
const Home = lazy(() => import('./pages/Home'))
const Chat = lazy(() => import('./pages/Chat'))
const Console = lazy(() => import('./pages/Console'))
const ConsoleAccount = lazy(() => import('./pages/console/Account'))
const ConsoleSearch = lazy(() => import('./pages/console/Search'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const MCP = lazy(() => import('./pages/MCP'))
const MarkdownPage = lazy(() => import('./pages/MarkdownPage'))
const TurbopufferUseCase = lazy(() => import('./pages/use-case/turbopuffer'))
const RimeUseCase = lazy(() => import('./pages/use-case/rime'))
const Blog = lazy(() => import('./pages/Blog'))
const InstallMCP = lazy(() => import('./pages/InstallMCP'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const Status = lazy(() => import('./pages/Status'))
const NotFound = lazy(() => import('./pages/NotFound'))
const AnalyticsWrapper = lazy(() => import('./components/AnalyticsWrapper'))

// Auth components
import { AuthRedirect } from './components/Auth/AuthRedirect'
import { PrivateRoute } from './components/Auth/PrivateRoute'
import { SignIn } from './components/Auth/SignIn'
import { SignUp } from './components/Auth/SignUp'
import { ResetPassword } from './components/Auth/ResetPassword'
import { PasswordResetConfirm } from './components/Auth/PasswordResetConfirm'

// Lazy load account pages
const Subscribe = lazy(() => import('./pages/Subscribe'))
const YourResources = lazy(() => import('./pages/YourResources'))

import ApiKeyManager from './components/ApiKeys/ApiKeyManager'
import { FeatureFlagDebugger } from './components/FeatureFlagDebugger'
import {
  Favicon,
  OpenGraphDefault,
  OpenGraphRime,
  OpenGraphTurbopuffer,
  OpenGraphHowRefMcp,
  OpenGraphHowMakeSearchGood,
} from './components/Helmets'
import RecentActivity from './components/RecentActivity'
import ScrollToTop from './components/ScrollToTop'
import { useAuth } from './hooks/useAuth'
import HowMakeSearchGood from './pages/blog/HowMakeSearchGood'
import HowDoesRefMcp from './pages/blog/HowDoesRefMcp'

function App() {
  const { user, loading } = useAuth()

  return (
    <BrowserRouter>
      <AnalyticsWrapper user={user} loading={loading} />
      <Favicon />
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/chat" element={<Chat />} />
        <Route path="/demo" element={<Chat isDemo />} />
        <Route path="/chat/:moduleId" element={<Chat />} />
        <Route path="/mcp" element={<MCP />} />
        <Route path="/docs" element={<MarkdownPage url="/docs.md" />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/status" element={<Status />} />
        <Route path="/blog" element={<Blog />} />
        <Route
          path="/blog/how-make-search-good"
          element={
            <>
              <title>How make search good?</title>
              <OpenGraphHowMakeSearchGood />
              <HowMakeSearchGood />
            </>
          }
        />
        <Route
          path="/blog/how-does-ref-mcp"
          element={
            <>
              <title>How Ref uses MCP to build the best possible documentation search</title>
              <OpenGraphHowRefMcp />
              <HowDoesRefMcp />
            </>
          }
        />
        <Route
          path="/use-case/turbopuffer"
          element={
            <>
              <OpenGraphTurbopuffer />
              <TurbopufferUseCase />
            </>
          }
        />
        <Route
          path="/use-case/rime"
          element={
            <>
              <OpenGraphRime />
              <RimeUseCase />
            </>
          }
        />
        <Route
          path="/"
          element={
            <>
              <OpenGraphDefault />
              <Home />
            </>
          }
        />

        {/* Authentication routes */}
        <Route
          path="/login"
          element={
            <AuthRedirect>
              <SignIn />
            </AuthRedirect>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRedirect>
              <SignUp />
            </AuthRedirect>
          }
        />
        <Route
          path="/auth/reset-password"
          element={
            <AuthRedirect>
              <ResetPassword />
            </AuthRedirect>
          }
        />
        <Route path="/auth/action" element={<PasswordResetConfirm />} />

        <Route path="/subscribe" element={<Subscribe />} />

        {/* Protected routes */}
        <Route element={<PrivateRoute user={user} loading={loading} />}>
          <Route path="/console" element={<Console />} />
          <Route
            path="/dashboard"
            element={
              <Console>
                <Dashboard />
              </Console>
            }
          />
          <Route
            path="/account"
            element={
              <Console>
                <ConsoleAccount />
              </Console>
            }
          />
          <Route
            path="/activity"
            element={
              <Console>
                <RecentActivity />
              </Console>
            }
          />
          <Route
            path="/keys"
            element={
              <Console>
                <ApiKeyManager />
              </Console>
            }
          />
          <Route
            path="/search"
            element={
              <Console>
                <ConsoleSearch />
              </Console>
            }
          />
          <Route
            path="/install"
            element={
              <Console>
                <InstallMCP />
              </Console>
            }
          />
          <Route
            path="/resources"
            element={
              <Console>
                <YourResources />
              </Console>
            }
          />
        </Route>

        {/* Catch-all route for 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <FeatureFlagDebugger />
    </BrowserRouter>
  )
}

export default App
